import dayjs from 'dayjs';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Alert } from '@mui/material';
import { SVGIconComponent } from '../../icons/types';
import { StatusBadge } from '../../status-badge/status-badge.component';
import { useHubCardHeaderStyles } from './hub-card-header.styles';
import { ActionsButton } from '../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../actions-button/actions-button.types';
import { SvgIconComponent } from '@mui/icons-material';
import { Colors } from '../../../../theme/colors';

export type HubCardHeaderProps = {
  hubName: string;
  hubStatus: string;
  statusColor: string;
  statusIcon: (() => JSX.Element) | SvgIconComponent | SVGIconComponent;
  deliveryContainerType: string;
  deliveryContainerCount: number;
  deliveryContainerCountsBySize: {
    Icon: SVGIconComponent;
    count: number;
    key: string;
  }[];
  actions: LoadedActionsButtonItem[];
  kioskLastConfigUpdatedTime?: Date;
  /** Optional label displayed on the right side of the header (e.g. "Preview") */
  isPreview?: boolean;
  /** Optional alert text displayed below the header content */
  alertText?: string;
  /** Optional alert icon displayed alongside the alert text */
  alertIcon?: React.ReactElement;
};

export function HubCardHeader(props: HubCardHeaderProps) {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useHubCardHeaderStyles();
  const statusTooltip = props.kioskLastConfigUpdatedTime
    ? `Last Sync: ${dayjs(props.kioskLastConfigUpdatedTime).format('DD MMM YYYY @ hh:mma')}`
    : undefined;

  return isXS ? (
    <Grid className={classes.root} container sx={{ flexDirection: 'column' }}>
      <Grid
        container
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        <Grid>
          <TitleSection {...props} />
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <Grid
      className={classes.root}
      container
      sx={{
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}
    >
      <Grid>
        <TitleSection {...props} />
      </Grid>
      <Grid>
        <IconSection deliveryContainerCountsBySize={props.deliveryContainerCountsBySize} />
      </Grid>
      {props.isPreview ? (
        <Grid style={{ marginLeft: 'auto' }}>
          <Typography variant="h6" style={{ color: Colors.collectedGreen }}>
            Preview
          </Typography>
        </Grid>
      ) : (
        <Grid>
          <Grid
            container
            className={classes.controls}
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Grid className={classes.paddedControls}>
              <StatusBadge
                color={props.statusColor}
                cornerRounding="max"
                Icon={props.statusIcon}
                label={props.hubStatus}
                tooltipLabel={statusTooltip}
                showTooltip={props.kioskLastConfigUpdatedTime !== undefined}
                capitalize
                desktopWidth="90px"
                fontSize="12px"
              />
            </Grid>
            <Grid className={classes.paddedControls}>
              <ActionsButton variant="light" itemArray={props.actions}></ActionsButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {props.alertText && (
        <Grid style={{ paddingTop: '8px' }} size={12}>
          <Alert variant="outlined" severity="warning" color="error" icon={props.alertIcon}>
            {props.alertText}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
}

function TitleSection({
  hubName,
  statusColor,
  hubStatus,
  deliveryContainerCount,
  deliveryContainerType
}: HubCardHeaderProps) {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useHubCardHeaderStyles();
  return (
    <>
      <p className={classes.hubName}>{hubName}</p>
      <p className={classes.lockerCount}>
        {deliveryContainerCount === 0
          ? `No ${deliveryContainerType}s yet`
          : `${deliveryContainerCount} ${deliveryContainerType ?? 'container'}${deliveryContainerCount > 1 ? 's' : ''}`}
        {isXS ? (
          <StatusBadge
            inline
            style={{ marginLeft: '5px' }}
            collapse="wrap-text"
            mobileWidth="56px"
            color={statusColor}
            cornerRounding="min"
            label={hubStatus}
            uppercase
            fontSize="10px"
          />
        ) : null}
      </p>
    </>
  );
}

function IconSection({ deliveryContainerCountsBySize }: Pick<HubCardHeaderProps, 'deliveryContainerCountsBySize'>) {
  const classes = useHubCardHeaderStyles();
  return (
    <Grid
      className={classes.utilisationCount}
      container
      direction="row"
      wrap="nowrap"
      sx={{
        justifyContent: 'center'
      }}
    >
      {deliveryContainerCountsBySize.map(({ Icon, count, key }) => (
        <Grid
          key={key}
          className={classes.utilisationCountIconWrapper}
          container
          direction="row"
          wrap="nowrap"
          spacing={1}
          sx={{
            alignItems: 'flex-end'
          }}
        >
          <Grid>{Icon ? <Icon className={classes.utilisationCountIcon} /> : <>{key}</>}</Grid>
          <Grid>{count}</Grid>
        </Grid>
      ))}
    </Grid>
  );
}
